/**
 * Route-visit container spans for RPC tracing.
 *
 * Sentry bills one event per *transaction* (root span); child spans ship
 * free inside their transaction. RPCs fired while a pageload/navigation
 * IdleTransaction is alive nest under it, but most of our RPCs fire long
 * after that window (question answers, tracking events, polls). Promoting
 * each of those to its own root transaction (`forceTransaction`) blew the
 * Sentry transaction quota, and letting Sentry infer a parent from the
 * ambient scope mis-parents spans launched from another RPC's async
 * continuation (the browser SDK has no async-context tracking), losing
 * them when that parent ships first.
 *
 * Instead, this manager assigns every RPC an explicit parent:
 *
 *  - the live pageload/navigation root while one is recording, else
 *  - a shared "container" root span covering the rest of the route visit,
 *    created lazily on the first RPC and reused until the next navigation.
 *
 * Containers close gracefully: a navigation, the tab being hidden, or
 * exceeding the maximum age marks the container closing, and it ends
 * once its last in-flight RPC finishes — so a span can never outlive
 * its shipped transaction. Page hide (the tab actually going away) ends
 * everything immediately, deliberately dropping still-in-flight child
 * spans: shipping the container with its completed spans beats losing
 * the whole thing.
 */

/** The subset of Sentry's Span that the manager needs. */
export interface ContainerSpan {
  end(endTimestamp?: number): void;
}

export interface RouteContainerDeps {
  /**
   * Create a detached root span (billed as its own transaction) covering
   * a slice of the current route visit.
   */
  startContainer(name: string): ContainerSpan;
  now(): number;
}

/**
 * Rotate containers older than this so a killed tab loses at most the
 * final slice of spans. Containers end at their last activity time
 * (Sentry's idle-span trim-end semantics), so a long idle gap before the
 * rotating RPC does not inflate the previous container's duration.
 */
export const CONTAINER_MAX_AGE_MS = 60_000;

export const FALLBACK_ROUTE_NAME = 'unknown-route';

interface Container {
  span: ContainerSpan;
  startedAt: number;
  lastActivityAt: number;
  inFlight: number;
  closing: boolean;
  ended: boolean;
}

export interface RpcHandle {
  /** The span the RPC should be parented to. */
  parent: unknown;
  /** Must be called exactly once when the RPC settles. */
  done(): void;
}

export class RouteContainerManager {
  private idleRoot?: unknown;
  private lastRouteName = FALLBACK_ROUTE_NAME;
  private current?: Container;
  private closingContainers = new Set<Container>();

  constructor(private deps: RouteContainerDeps) {}

  noteIdleRootStart(root: unknown, name: string): void {
    this.idleRoot = root;
    this.lastRouteName = name;
    // A new pageload/navigation root means a new route — close the
    // previous route's container so it never carries a stale name into
    // the new route (resolver RPCs can open one between NavigationStart
    // and the navigation span starting).
    this.closeCurrent();
  }

  /**
   * `finalName` is the root's name at the moment it ended. Navigation and
   * pageload spans start named with the raw URL and are renamed to the
   * parameterized route (e.g. `/study/:id`) by Sentry's TraceService on
   * ResolveEnd — after `noteIdleRootStart` recorded the raw one — so the
   * end-time name is the one containers should inherit.
   */
  noteIdleRootEnd(root: unknown, finalName?: string): void {
    if (this.idleRoot === root) {
      this.idleRoot = undefined;
      if (finalName !== undefined) {
        this.lastRouteName = finalName;
      }
    }
  }

  beginRpc(): RpcHandle {
    const now = this.deps.now();
    // Force-end any closing container whose in-flight RPC never settled
    // (a hung/black-holed request would otherwise pin it — and its Sentry
    // span — open until the tab is hidden). RPC traffic drives this sweep,
    // which is exactly when such a leak would accumulate.
    for (const container of this.closingContainers) {
      if (now - container.lastActivityAt > 2 * CONTAINER_MAX_AGE_MS) {
        this.endContainer(container);
      }
    }

    if (this.idleRoot !== undefined) {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return { parent: this.idleRoot, done: () => {} };
    }

    if (this.current && now - this.current.startedAt > CONTAINER_MAX_AGE_MS) {
      this.closeWhenDrained(this.current);
      this.current = undefined;
    }
    if (!this.current) {
      this.current = {
        span: this.deps.startContainer(this.lastRouteName),
        startedAt: now,
        lastActivityAt: now,
        inFlight: 0,
        closing: false,
        ended: false,
      };
    }

    const container = this.current;
    container.inFlight++;
    container.lastActivityAt = now;
    let settled = false;
    return {
      parent: container.span,
      done: () => {
        if (settled) return;
        settled = true;
        container.inFlight--;
        container.lastActivityAt = this.deps.now();
        if (container.closing && container.inFlight === 0) {
          this.endContainer(container);
        }
      },
    };
  }

  navigationStarted(): void {
    this.closeCurrent();
  }

  /**
   * Close the current container gracefully: no new RPCs join it, and it
   * ends once its last in-flight RPC finishes.
   */
  closeCurrent(): void {
    if (this.current) {
      this.closeWhenDrained(this.current);
      this.current = undefined;
    }
  }

  pageHidden(): void {
    if (this.current) {
      this.endContainer(this.current);
      this.current = undefined;
    }
    for (const container of [...this.closingContainers]) {
      this.endContainer(container);
    }
  }

  private closeWhenDrained(container: Container): void {
    container.closing = true;
    if (container.inFlight === 0) {
      this.endContainer(container);
    } else {
      this.closingContainers.add(container);
    }
  }

  private endContainer(container: Container): void {
    if (container.ended) return;
    container.ended = true;
    container.closing = true;
    this.closingContainers.delete(container);
    // Trim to the last RPC activity so an idle gap before whatever
    // triggered the end (rotation, navigation, hide) doesn't inflate the
    // container's duration.
    container.span.end(container.lastActivityAt);
  }
}
